/** AttendanceComplianceRulesEditor — workspace default + per-department overrides
 *  Renders inside the HR Settings tab. HR-only (checked server-side).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { ShieldCheck, Plus, Trash2, Save } from "lucide-react";

const FIELDS = [
  ["enabled", "Enabled", "checkbox"],
  ["late_grace_minutes", "Late grace (min)", "number"],
  ["late_repeat_threshold", "Late repeat threshold (per range)", "number"],
  ["min_hours_per_day", "Min hours / day", "number"],
  ["flag_missed_checkin", "Flag missed check-in", "checkbox"],
  ["flag_missed_checkout", "Flag missed check-out", "checkbox"],
  ["flag_short_day", "Flag short day", "checkbox"],
  ["flag_anomalies", "Flag anomalies", "checkbox"],
  ["geofence_required", "Geofence required", "checkbox"],
  ["weekend_work_review", "Weekend/off-day work review", "checkbox"],
];

export default function AttendanceComplianceRulesEditor({ wsId }) {
  const [ws, setWs] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [newDept, setNewDept] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/attendance/compliance-rules`).then((r) => {
      setWs(r.data.workspace); setOverrides(r.data.department_overrides || []); setDirty(false);
    }).catch(() => {});
    api.get(`/workspaces/${wsId}/departments`).then((r) => setDepartments(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId]);

  const saveWs = async () => {
    setSaving(true);
    try {
      await api.put(`/workspaces/${wsId}/attendance/compliance-rules`, { department_id: null, ...ws });
      toast.success("Workspace compliance rules saved");
      setDirty(false); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const saveOverride = async (ov) => {
    try {
      await api.put(`/workspaces/${wsId}/attendance/compliance-rules`, ov);
      toast.success(`${ov.department_id} override saved`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };
  const removeOverride = async (dept) => {
    try { await api.delete(`/workspaces/${wsId}/attendance/compliance-rules/${encodeURIComponent(dept)}`);
      toast.success("Override removed"); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const addOverride = () => {
    if (!newDept) return;
    if (overrides.some((o) => o.department_id === newDept)) { toast.error("Override already exists"); return; }
    saveOverride({ department_id: newDept, ...ws });
    setNewDept("");
  };

  if (!ws) return <div className="text-sm text-muted-foreground">Loading compliance rules…</div>;

  return (
    <div className="border p-4 space-y-4" data-testid="compliance-rules-editor">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4" />
        <div className="font-bold text-sm">Attendance compliance rules</div>
        <span className="text-[10px] text-muted-foreground">Workspace defaults · departments can override individual fields</span>
      </div>
      <RuleBlock rules={ws} onChange={(k, v) => { setWs({ ...ws, [k]: v }); setDirty(true); }} testidPrefix="rule-ws" />
      <div className="flex gap-2 pt-1">
        <Button size="sm" className="rounded-none" onClick={saveWs} disabled={!dirty || saving} data-testid="rule-ws-save">
          <Save className="w-3.5 h-3.5 mr-1" />{saving ? "Saving…" : "Save workspace defaults"}
        </Button>
      </div>

      <div className="border-t pt-4">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Department overrides ({overrides.length})</div>
        <div className="space-y-3" data-testid="rule-overrides">
          {overrides.map((ov) => (
            <div key={ov.department_id} className="border p-3 space-y-2" data-testid={`rule-override-${ov.department_id}`}>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="rounded-none text-[10px]">{ov.department_id}</Badge>
                <Button size="sm" variant="ghost" className="rounded-none h-6 ml-auto text-red-500" onClick={() => removeOverride(ov.department_id)} data-testid={`rule-override-del-${ov.department_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              <OverrideRow ov={ov} onSave={saveOverride} />
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-center mt-3 pt-3 border-t">
          <Select value={newDept} onValueChange={setNewDept}>
            <SelectTrigger className="h-8 w-56 rounded-none text-xs" data-testid="rule-override-newdept"><SelectValue placeholder="Pick department to override…" /></SelectTrigger>
            <SelectContent className="max-h-64">
              {departments.filter((d) => !overrides.some((o) => o.department_id === d.name)).map((d) => (
                <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={addOverride} disabled={!newDept} data-testid="rule-override-add">
            <Plus className="w-3.5 h-3.5 mr-1" />Add override
          </Button>
        </div>
      </div>
    </div>
  );
}

function OverrideRow({ ov, onSave }) {
  const [row, setRow] = useState(ov);
  const [dirty, setDirty] = useState(false);
  return (
    <>
      <RuleBlock rules={row} onChange={(k, v) => { setRow({ ...row, [k]: v }); setDirty(true); }} testidPrefix={`rule-ov-${ov.department_id}`} />
      <div className="flex justify-end">
        <Button size="sm" variant="outline" className="rounded-none h-7" disabled={!dirty} onClick={() => { onSave(row); setDirty(false); }} data-testid={`rule-ov-save-${ov.department_id}`}>
          <Save className="w-3.5 h-3.5 mr-1" />Save
        </Button>
      </div>
    </>
  );
}

function RuleBlock({ rules, onChange, testidPrefix }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
      {FIELDS.map(([k, label, type]) => (
        <div key={k}>
          <div className="text-[10px] uppercase text-muted-foreground mb-1">{label}</div>
          {type === "checkbox" ? (
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!rules[k]} onChange={(e) => onChange(k, e.target.checked)} data-testid={`${testidPrefix}-${k}`} />
              <span>{rules[k] ? "On" : "Off"}</span>
            </label>
          ) : (
            <Input type="number" value={rules[k] ?? ""} onChange={(e) => onChange(k, Number(e.target.value))} className="h-8 rounded-none font-mono" data-testid={`${testidPrefix}-${k}`} />
          )}
        </div>
      ))}
    </div>
  );
}
